import React from 'react'; 
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import ToastContainer from './components/ui/ToastContainer';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './layouts/DashboardLayout';

// Pages
import Login from './pages/Login';
import DashboardHome from './pages/DashboardHome';
import StudentList from './pages/students/StudentList';
import DriverList from './pages/drivers/DriverList';
import BusList from './pages/buses/BusList';
import PaymentList from './pages/payments/PaymentList';
import PaymentModeList from './pages/payments/PaymentModeList';
import BankList from './pages/payments/BankList';
import CashPaymentsList from './pages/payments/CashPaymentsList';
import RegionList from './pages/regions/RegionList';
import SchoolClassList from './pages/classes/SchoolClassList';
import InstitutionList from './pages/institutions/InstitutionList';
import QuickPayment from './pages/payments/QuickPayment';
import SettingsPage from './pages/settings/SettingsPage';
import AcademicYearList from './pages/academic-years/AcademicYearList';
import StaffList from './pages/staff/StaffList';
import PartnerList from './pages/partners/PartnerList';
import SupplierList from './pages/suppliers/SupplierList';
import ExpenseTypeList from './pages/expenses/ExpenseTypeList';
import ExpenseList from './pages/expenses/ExpenseList';
import YearSelection from './pages/YearSelection';
import Scanner from './pages/students/Scanner';
import StudentCardsPrint from './pages/students/StudentCardsPrint';
import AttendanceList from './pages/attendance/AttendanceList';
import AttendanceScanner from './pages/attendance/AttendanceScanner';
import GroupList from './pages/groups/GroupList';
import PaymentReceiptPrint from './pages/payments/PaymentReceiptPrint';
import PaymentsReportPrint from './pages/payments/PaymentsReportPrint';
import DepositReportList from './pages/payments/DepositReportList';
import DepositReportPrint from './pages/payments/DepositReportPrint';
import StudentStatusPrint from './pages/students/StudentStatusPrint';
import UnpaidStudentsPrint from './pages/payments/UnpaidStudentsPrint';
import PaymentStatusPrint from './pages/payments/PaymentStatusPrint';
import BankListPrint from './pages/payments/BankListPrint';
import DepositReportListPrint from './pages/payments/DepositReportListPrint';
import PrintCashPayment from './pages/payments/PrintCashPayment';
import BeneficiariesStatsPrint from './pages/payments/BeneficiariesStatsPrint';
import FinancialReports from './pages/reports/FinancialReports';
import PrintFinancialReport from './pages/reports/PrintFinancialReport';
import PrintExpensesDetail from './pages/reports/PrintExpensesDetail';
import PrintRevenuesDetail from './pages/reports/PrintRevenuesDetail';
import PrintBusExpenses from './pages/reports/PrintBusExpenses';
import DisciplineHome from './pages/discipline/DisciplineHome';
import PrintWarning from './pages/discipline/PrintWarning';
import PrintPunishment from './pages/discipline/PrintPunishment';
import { TimetableList } from './pages/timetables/TimetableList';
import { TimetablePrint } from './pages/timetables/TimetablePrint';

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <BrowserRouter>
          <ToastContainer />
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<Login />} />
            
            <Route 
              path="/print/financial-report" 
              element={
                <ProtectedRoute>
                  <PrintFinancialReport />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/print/expenses-detail" 
              element={
                <ProtectedRoute>
                  <PrintExpensesDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/print/revenues-detail" 
              element={
                <ProtectedRoute>
                  <PrintRevenuesDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/print/bus-expenses" 
              element={
                <ProtectedRoute>
                  <PrintBusExpenses />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/print/student-cards" 
              element={
                <ProtectedRoute>
                  <StudentCardsPrint />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/print/students-filtered" 
              element={
                <ProtectedRoute>
                  <StudentStatusPrint />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/print/payment-receipt" 
              element={
                <ProtectedRoute>
                  <PaymentReceiptPrint />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/print/payments-report" 
              element={
                <ProtectedRoute>
                  <PaymentsReportPrint />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/print/deposit-report" 
              element={
                <ProtectedRoute>
                  <DepositReportPrint />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/print/unpaid-students" 
              element={
                <ProtectedRoute>
                  <UnpaidStudentsPrint />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/print/payment-status" 
              element={
                <ProtectedRoute>
                  <PaymentStatusPrint />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/print/bank-list" 
              element={
                <ProtectedRoute>
                  <BankListPrint />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/print/deposit-reports-list" 
              element={
                <ProtectedRoute>
                  <DepositReportListPrint />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/print/cash-payment/:id" 
              element={
                <ProtectedRoute>
                  <PrintCashPayment />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/print/beneficiaries-stats" 
              element={
                <ProtectedRoute>
                  <BeneficiariesStatsPrint />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/print/warning/:id" 
              element={
                <ProtectedRoute>
                  <PrintWarning />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/print/punishment/:id" 
              element={
                <ProtectedRoute>
                  <PrintPunishment />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/print/timetables"
              element={
                <ProtectedRoute>
                  <TimetablePrint />
                </ProtectedRoute>
              }
            />

            <Route 
              path="/select-year" 
              element={
                <ProtectedRoute>
                  <YearSelection />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/dashboard/*" 
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Routes>
                      <Route index element={<DashboardHome />} />
                      <Route path="financial-reports" element={<FinancialReports />} />
                      <Route path="students" element={<StudentList />} />
                      <Route path="drivers" element={<DriverList />} />
                      <Route path="buses" element={<BusList />} />
                      <Route path="payments" element={<PaymentList />} />
                      <Route path="bank" element={<BankList />} />
                      <Route path="cash-payments" element={<CashPaymentsList />} />
                      <Route path="payment-modes" element={<PaymentModeList />} />
                      <Route path="regions" element={<RegionList />} />
                      <Route path="academic-years" element={<AcademicYearList />} />
                      <Route path="school-classes" element={<SchoolClassList />} />
                      <Route path="timetables" element={<TimetableList />} />
                      <Route path="institutions" element={<InstitutionList />} />
                      <Route path="staff" element={<StaffList />} />
                      <Route path="partners" element={<PartnerList />} />
                      <Route path="suppliers" element={<SupplierList />} />
                      <Route path="expense-types" element={<ExpenseTypeList />} />
                      <Route path="expenses" element={<ExpenseList />} />
                      <Route path="quick-payment/:qrcode" element={<QuickPayment />} />
                      <Route path="scanner" element={<Scanner />} />
                      <Route path="attendance" element={<AttendanceList />} />
                      <Route path="attendance/scanner" element={<AttendanceScanner />} />
                      <Route path="groups" element={<GroupList />} />
                      <Route path="deposit-reports" element={<DepositReportList />} />
                      <Route path="discipline" element={<DisciplineHome />} />
                      <Route path="settings" element={<SettingsPage />} />
                    </Routes>
                  </DashboardLayout>
                </ProtectedRoute>
              } 
            />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
